'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ClearIcon from '@mui/icons-material/Clear';
import MenuIcon from '@mui/icons-material/Menu';
import { css, styled } from '@mui/material/styles';
import React from 'react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { excludeComponentProps } from '../utils/excludeComponentProps';
import { HeaderAppend } from './HeaderAppend';
import { HeaderDivider } from './HeaderDivider';
import { HeaderLogo } from './HeaderLogo';
import { HeaderNavigation } from './HeaderNavigation';
import { HeaderSearch } from './HeaderSearch';
import { useHeaderState } from './HeaderStateContext';
import { HeaderTitle } from './HeaderTitle';
import { headerCssClassNames } from './headerCssClassNames';
import clsx from 'clsx';
import { HeaderContainer } from './HeaderContainer';
const StyledHeaderLink = styled(Link)({
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center'
});
const StyledHeader = styled('header', excludeComponentProps('showTitle', 'isSticky'))(({ theme, showTitle, isSticky }) => {
    const drawerTop = showTitle ?
        'calc(var(--header-bar-height) + var(--header-append-height))' :
        'var(--header-bar-height)';
    return {
        height: 'var(--header-bar-height)',
        backgroundColor: '#fff',
        borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
        boxShadow: 'none',
        position: 'relative',
        gridArea: 'header',
        '~ .MuiDrawer-modal': {
            top: drawerTop,
            height: `calc(100% - ${drawerTop})`,
            '.MuiDrawer-modal, .MuiBackdrop-root, .MuiDrawer-paper': {
                top: drawerTop,
                height: `calc(100% - ${drawerTop})`
            }
        },
        ...isSticky && {
            position: 'sticky',
            top: 0,
            zIndex: `${theme.zIndex.appBar}`
        }
    };
});
const EndContent = styled('div')(({ theme }) => css `
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
    margin-right: -${theme.tokens.FuelSpacing2};
`);
const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => !['leftSide'].includes(String(prop))
})(({ leftSide }) => leftSide ? {
    marginRight: `-${1.5 / 2}rem`
} : null);
export const HeaderBar = React.forwardRef(({ className, children, ...props }, ref) => {
    const state = useHeaderState();
    const hasItems = Array.isArray(state.navigationItems) && state.navigationItems.length > 0;
    const showDesktopNavigation = !state.hideNavigation && hasItems;
    const showMobileMenu = !state.hideMobileMenu && hasItems && (state.isSmallMobile || state.isMobile || state.isTablet);
    const showLeftMobileMenuButton = showMobileMenu && state.mobileMenuPosition === 'left';
    const showRightMobileMenuButton = showMobileMenu && state.mobileMenuPosition === 'right';
    const isMobile = state.isSmallMobile || state.isMobile;
    const showTitle = isMobile;
    const { closeText = 'Close Menu', openText = 'Open Menu', ...HeaderMenuButtonProps } = state.HeaderMenuButtonProps ?? {};
    const renderMobileIcon = () => {
        return (_jsx(StyledIconButton, { ...HeaderMenuButtonProps, "aria-expanded": state.isMobileMenuOpen, "aria-label": state.isMobileMenuOpen ? closeText : openText, className: headerCssClassNames.mobileMenuButton, leftSide: state.mobileMenuPosition === 'left', onClick: (clickEvent) => {
                state.handleMobileMenuClick(clickEvent);
                state.isMobileMenuOpen ?
                    state.handleMobileMenuClose(clickEvent) :
                    state.handleMobileMenuOpen(clickEvent);
            }, children: state.isMobileMenuOpen ? _jsx(ClearIcon, {}) : _jsx(MenuIcon, {}) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(StyledHeader, { ...props, className: clsx(headerCssClassNames.bar, className), "data-testid": 'header-bar', isSticky: state.isSticky, ref: ref, showTitle: Boolean(showTitle && state.title), children: _jsxs(HeaderContainer, { className: headerCssClassNames.barContainer, isContained: state.isContained, children: [showLeftMobileMenuButton && renderMobileIcon(), !state.hideBranding && (_jsx(StyledHeaderLink, { "aria-label": state.logoLinkText, className: headerCssClassNames.link, component: state.HeaderLogoLinkComponent, href: state.logoLink, ...state.HeaderLogoLinkProps, children: _jsx(HeaderLogo, {}) })), !isMobile && state.title && !state.hideBranding && (_jsx(HeaderDivider, {})), !isMobile && state.title && (_jsx(StyledHeaderLink, { "aria-label": state.title, className: headerCssClassNames.link, component: state.HeaderTitleLinkComponent, href: state.titleLink, ...state.HeaderTitleLinkProps, children: _jsx(HeaderTitle, {}) })), _jsxs(EndContent, { children: [state.showSearch && _jsx(HeaderSearch, {}), children, showRightMobileMenuButton && renderMobileIcon()] })] }) }), isMobile && state.title && _jsx(HeaderAppend, {}), showDesktopNavigation && _jsx(HeaderNavigation, {})] }));
});
HeaderBar.displayName = 'HeaderBar';
